<template>
  <v-card>
    <v-toolbar color="grey-lighten-1 flex-grow-0">
      <v-toolbar-title>{{ $t('basketModal.heading') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon :title="$t('general.close')" :disabled="basketPending" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card-text class="d-flex flex-wrap" :class="$style.content">
      <v-overlay absolute :value="loading">
        <v-progress-circular :size="64" :indeterminate="loading" />
      </v-overlay>
      <div :class="['col-12', { ['col-lg-8']: renderAccessory }]">
        <div :class="$style.bikeForm">
          <section class="d-sm-flex">
            <div class="pr-2 flex-grow-1">
              <h3>
                <span :class="$style.name" class="ml-1">{{ name }}</span
                ><br />
                <span :class="$style.code" class="ml-1"
                  >({{ $t('product.ordernumber', { code: code }) }})</span
                >
              </h3>
              <ul v-if="!dataLoading" class="mb-3 unstyled-list">
                <li v-if="!hasSoldOutSelling && isFirstAvailabilityAvailable">
                  {{
                    $tc(
                      `basketModal.${
                        firstAvailabilityIsReal ? 'currentlyAvailable' : 'maybeAvailable'
                      }`,
                      availableStock,
                      {
                        total: availableStockText,
                      },
                    )
                  }}
                </li>
                <li v-show="!hasSoldOutSelling">
                  {{ $t('basketModal.futureEstimatedAvailable', { total: futureStockText }) }}
                </li>
              </ul>
              <v-skeleton-loader v-else width="50%" type="text@2"></v-skeleton-loader>
            </div>
            <div class="pl-sm-2 flex-grow-1" :class="$style.asapContainer">
              <h3>{{ $t('basketModal.orderAsapHeadline') }}</h3>
              <QuantityButtons
                ref="asapInput"
                v-model="asap"
                class="ml-n2"
                type="number"
                :max="asapMax"
                :show-max="!hasSoldOutSelling"
                :min="futureTotal || (asapMax === 0 ? 0 : 1)"
                color="primary"
                :disabled="dataLoading"
                :label="
                  $tc('basketModal.amountLabel', hasSoldOutSelling ? 0 : asapMax, {
                    max: asapMaxText,
                  })
                "
              />
            </div>
          </section>
          <section v-if="batteryUpgradeOptions.length" :class="$style.batteryOptions">
            <h3 class="mb-0 text--primary">
              {{ $t('basketModal.batteryUpgradeHeadline') }}
            </h3>
            <v-radio-group v-model="batteryUpgradeChoice">
              <v-radio
                v-for="option in batteryUpgradeOptions"
                :key="option.value"
                :value="option.value"
                :title="option.sku"
                :disabled="option.disabled"
              >
                <template #label>
                  <div class="d-flex align-center">
                    <span class="mr-3" :class="$style.batteryOption"
                      >{{ option.value }} {{ $t('term.wh') }} (+&nbsp;{{
                        $n(option.upgrade_price, {
                          key: 'currency',
                          currency: batteryUpgradeCurrency,
                        })
                      }})</span
                    >
                    <AvailabilityBadge v-bind="option.availabilityData" />
                  </div> </template
              ></v-radio>
            </v-radio-group>
          </section>
          <section v-show="asap" :class="$style.splitArea">
            <h3 :class="$style.headerSendAsap" class="mb-0 text--primary">
              {{ $t('basketModal.sendAsSoonAsPossible') }}
            </h3>
            <div :class="$style.availableAsap">
              <h4>
                <v-icon color="primary" large>mdi-arrow-right-bold</v-icon>
                {{ $t('basketModal.fromWarehouse') }}
              </h4>
              <p class="mb-2 d-flex align-center">
                {{ $tc('basketModal.currentlyAvailable', 0) }}
                <QuantityButtons
                  v-model="fromWarehouse"
                  type="number"
                  :max="availableStock"
                  :min="0"
                  color="primary"
                  :disable="!!futureAsapTotal || dataLoading"
                >
                  <template #default="">
                    <b v-if="remainingAsapFromAvailable === asapFromAvailable">{{
                      remainingAsapFromAvailable
                    }}</b>
                    <b v-else>{{ remainingAsapFromAvailable }}/{{ asapFromAvailable }}</b>
                  </template>
                </QuantityButtons>
              </p>
            </div>
            <div v-show="futureStock" :class="$style.futureAsap">
              <h4>
                <v-icon color="primary" large>mdi-arrow-right-bold</v-icon>
                {{ $t('basketModal.fromPlannedProductions') }}
              </h4>
              <ul class="unstyled-list">
                <li v-for="item in future" v-show="item.asap" :key="item.id" class="mb-2">
                  {{
                    $t('basketModal.estimatedFromPlanned', {
                      week: item.week,
                      year: item.year,
                      dateMonday: $d(item.monday),
                    })
                  }}
                  <b>{{ item.asap }}</b>
                </li>
              </ul>
            </div>
            <div v-show="showDateSelectionSwitch" :class="$style.headerDateSel">
              <v-switch v-model="showDateSelection" class="mt-0" inset
                ><template #label
                  ><h3
                    :class="
                      showDateSelection
                        ? `mb-0 text--primary ${$style.showDateSelectionLabel}`
                        : `mb-0 ${$style.showDateSelectionLabel}`
                    "
                  >
                    {{ $t('basketModal.sendItemsOnSpecificDates') }}
                  </h3></template
                ></v-switch
              >
            </div>
            <div
              v-show="showDateSelection && productOrderDateAttribute"
              :class="$style.dateSelAvailable"
            >
              <section v-if="deliveryDateMax">
                <ul class="unstyled-list">
                  <li v-for="(entry, index) in available" :key="entry.id" class="d-flex">
                    <v-menu
                      v-model="entry.dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          class="pt-3"
                          :value="formatDate(entry.date)"
                          :label="$t('basketModal.dateMaxThirtyDays')"
                          readonly
                          dense
                          hide-details
                          v-bind="attrs"
                          :disabled="dataLoading"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="entry.date"
                        :locale="locale"
                        :show-current="false"
                        :min="minDate"
                        :max="maxDate"
                        :first-day-of-week="1"
                        :allowed-dates="allowedDates"
                        no-title
                        :disabled="dataLoading"
                        @input="entry.dateMenu = false"
                      />
                    </v-menu>
                    <QuantityButtons
                      v-model="entry.amount"
                      class="ml-2"
                      :class="$style.amountButton"
                      type="number"
                      :max="entry.max"
                      :min="0"
                      color="primary"
                      :label="$tc('basketModal.amountLabel', entry.max, { max: entry.max })"
                      :disabled="dataLoading"
                    />
                    <v-btn
                      class="ml-4"
                      type="button"
                      dense
                      icon
                      :disabled="dataLoading"
                      @click="removeDateEntry(index)"
                    >
                      <IconSelect
                        icon="trash"
                        :height="24"
                        :width="24"
                        :icon-props="{ 'stroke-width': 1 }"
                      />
                    </v-btn>
                  </li>
                </ul>
                <v-btn
                  small
                  rounded
                  class="mb-2 btn primary--text"
                  :disabled="!canAddAnotherAvailableEntry && !dataLoading"
                  @click="addNewDateEntry"
                  ><v-icon dark>mdi-plus</v-icon>
                  {{ $tc('basketModal.addOrderDateEntry', available.length) }}</v-btn
                >
              </section>
            </div>
            <ul
              v-show="futureStock && showDateSelection && productOrderCwAttribute"
              :class="$style.dateSelFuture"
            >
              <li v-for="item in future" :key="item.id" :class="$style.dateSelFutureItem">
                <RawHtml
                  :template="`<span>${$t('basketModal.orderFromPlanned', {
                    week: item.week,
                    year: item.year,
                    dateMonday: $d(item.monday),
                  })}</span>`"
                />
                <QuantityButtons
                  v-model="item.amount"
                  class="ml-n2"
                  type="number"
                  :max="item.stock"
                  :min="0"
                  color="primary"
                  :disabled="dataLoading"
                  :label="
                    $tc('basketModal.amountLabel', hasSoldOutSelling ? 0 : item.stock, {
                      max: item.stockText,
                    })
                  "
                  @input="handleCwAmountChange(item)"
                />
              </li>
            </ul>
            <div v-if="!hasOneGroupPrice" :class="$style.groupPrices">
              <v-btn
                v-for="(price, key) in filteredGroupPrices"
                :key="key"
                type="button"
                class="btn align-self-end mt-3 px-4 py-2"
                color="primary"
                rounded
                :disabled="loading"
                :elevation="0"
                @click="submit($event, key)"
              >
                <BrandPrices class="mr-2" :group-prices="{ [key]: price }" :class="$style.price" />

                {{ $t(relatedIds.length ? 'basketModal.saveChanges' : 'basketModal.addToBasket') }}
              </v-btn>
            </div>
          </section>
        </div>
      </div>
      <ProducBasketFormAccessories
        v-if="renderAccessory"
        :accessory-products="accessoryProducts"
        :amounts="accessories"
        :loading="dataLoading"
        :class="$style.accessoryCard"
        class="col-12 col-lg-4 pl-0 pl-md-8"
        @input="accessoryAmountChange"
      ></ProducBasketFormAccessories>
    </v-card-text>
    <v-card-actions :class="$style.actions" class="px-6 d-flex justify-space-between">
      <span v-if="!dataLoading" class="mb-0 h3"
        >{{ $t('basketModal.sum') }}:
        {{ amountString }}
        <span v-if="asapAccessory">
          and
          {{ $tc('basketModal.amountAccessories', asapAccessory, { count: asapAccessory }) }}</span
        >
      </span>
      <v-skeleton-loader v-else width="5rem" type="text"></v-skeleton-loader>
      <span v-if="submitError">{{ $t('basketModal.submitError') }}</span>
      <v-btn
        v-if="hasOneGroupPrice"
        type="button"
        class="btn align-self-end"
        color="primary"
        rounded
        :disabled="loading"
        :elevation="0"
        @click="submit"
      >
        <!-- <BrandPrices class="mr-2" :group-prices="filteredGroupPrices" :class="$style.price" /> -->

        {{ $t(relatedIds.length ? 'basketModal.saveChanges' : 'basketModal.addToBasket') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
// import axios from '@/js/helper/axios-helper.js'
import {
  isEmpty,
  get,
  head,
  tail,
  cloneDeep,
  isUndefined,
  isNull,
  mapKeys,
  pick,
  compact,
  uniq,
  min,
} from 'lodash-es'
import { add, startOfToday, formatISO, parseISO, getDay, setDay, setYear, setWeek } from 'date-fns'
import { camelCase } from 'change-case'

import { mapActions, mapState } from 'vuex'
import { types as STORE_MODULES } from '@/js/plugins/store/store-modules.js'
import BIKE_ATTRIBUTES from '@/js/helper/bike-attributes.js'
import APP_CONSTANTS from '@/js/plugins/store/modules/app/constants.js'
// import PRODUCT_ACTIONS from '@/js/plugins/store/modules/products/action-types.js'
// import PRODUCT_GETTERS from '@/js/plugins/store/modules/products/getter-types.js'
// import AIMEOS_META_CONSTANTS from '@/js/helper/aimeos-meta-constants.js'
// import AIMEOS_GETTER_TYPES from '@/js/plugins/store/modules/aimeos/getter-types'
import AIMEOS_ACTIONS from '@/js/plugins/store/modules/aimeos/actions/actions-types.js'
import {
  AVAILABILITY_CONSTANTS,
  AVAILABILITY_LIMITS,
} from '@/js/plugins/store/modules/products/constants.js'
// import API_CONSTANTS from '@/js/helper/api-constants.js'
import FILTER_CONSTANTS from '@/js/helper/filter-constants.js'
import {
  COMPANY_CODE_DELIVERY_DATE_DAYS,
  CLIENT_TO_COMPANY_CODE,
} from '@/js/helper/dp-usermanagement-constants'
import { v1 as uuidv1 } from 'uuid'
import { iso8601WeekSettings } from '@/js/helper/date-helper.js'

import FallbackImage from '@/images/placeholder.png'
// import {
//   generateBasketAttributes,
//   generateBasketAttributesFromBasketinfo,
// } from '@/js/helper/product-data-helper.js'

import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'
import QuantityButtons from '@/js/components/Shared/QuantityButtons.vue'
import RawHtml from '@/js/components/Shared/RawHtml'
// import FeatureSwitch from '@/js/components/Shared/FeatureSwitch.js'
import BrandPrices from '@/js/components/ProductDetail/BrandPrices.vue'
import AvailabilityBadge from '@/js/components/Shared/Product/AvailabilityBadge.vue'
import ProducBasketFormAccessories from '@/js/components/ProductDetail/ProducBasketFormAccessories.vue'

import RequestOrderProductDataMixin from '@/js/mixins/request-order-product-data-mixin.js'
import AvailabilityMixin from '@/js/mixins/availability-mixin.js'
import ProductBatteryUpgradesMixin from '@/js/mixins/product-battery-upgrades-mixin.js'

const startData = {
  asap: 1,
  asapFromAvailable: 1,
  future: {},
  accessories: {},
}

const soldOutSellingMax = 50

export default {
  name: 'ProductBasketForm',
  components: {
    IconSelect,
    QuantityButtons,
    RawHtml,
    // FeatureSwitch,
    BrandPrices,
    ProducBasketFormAccessories,
    AvailabilityBadge,
  },
  mixins: [RequestOrderProductDataMixin, AvailabilityMixin, ProductBatteryUpgradesMixin],
  props: {
    articleType: {
      type: String,
      default: 'bike',
    },
    productId: {
      type: [String, Number],
      default: undefined,
    },
    productScope: {
      type: String,
      default: undefined,
    },
    code: {
      type: String,
      default: undefined,
    },
    name: {
      type: String,
      default: undefined,
    },
    basketValues: {
      type: Object,
      default: () => ({}),
    },
    basketAttributes: {
      type: Object,
      default: () => ({}),
    },
    basketLinkUrl: {
      type: String,
      default: undefined,
    },
    relatedIds: {
      type: Array,
      default: () => [],
    },
    showImage: {
      type: Boolean,
      default: false,
    },
    addToBasketCallback: {
      type: Function,
      default: () => {},
    },
    customBasketAttributes: {
      type: Object,
      default: () => ({}),
    },
    availabilityProp: {
      type: Object,
      default: () => ({}),
    },
    groupPrices: {
      type: Object,
      default: () => ({}),
    },
    accessoryProducts: {
      type: Array,
      default: () => [],
    },
    hasSoldOutSelling: {
      type: Boolean,
      default: false,
    },
    upgradeAttributes: {
      type: Object,
      default: () => ({}),
    },
    // groupPricesFormatted: {
    //   type: Object,
    //   default: () => ({}),
    // },
  },
  data() {
    return {
      fallbackImage: FallbackImage,
      ...cloneDeep(startData),
      available: [],
      minDate: formatISO(
        add(startOfToday(), {
          days: 1,
        }),
        { representation: 'date' },
      ),
      showDateSelection: false,
      basketPending: false,
      // productDataPending: false,
      availibilityPending: false,
      // requestedBasketAttributes: {},
      submitError: false,
      submitBrand: head(Object.keys(this.groupPrices)),
      batteryUpgradeChoice: undefined,
    }
  },
  computed: {
    ...mapState(STORE_MODULES.APP, {
      locale: APP_CONSTANTS.LOCALE,
    }),
    companyCodes() {
      return Object.keys(this.filteredGroupPrices).map(c => CLIENT_TO_COMPANY_CODE[c])
    },
    deliveryDateMax() {
      const days = uniq(
        this.companyCodes.map(c => COMPANY_CODE_DELIVERY_DATE_DAYS[this.articleType]?.[c]),
      )
      if (days.length > 1) {
        return 0
      }
      return head(compact(days)) || 0
    },
    maxDate() {
      return formatISO(
        add(startOfToday(), {
          days: this.deliveryDateMax,
        }),
        { representation: 'date' },
      )
    },
    amountString() {
      const kind =
        this.productScope === FILTER_CONSTANTS.SCOPE.SERVICE
          ? 'amountParts'
          : this.productScope === FILTER_CONSTANTS.SCOPE.ACCESSORIES
          ? 'amountAccessories'
          : 'amountBikes'
      return this.$tc(`basketModal.${kind}`, this.totalAmount, { count: this.totalAmount })
    },
    asapAccessory() {
      return Object.values(this.accessories).reduce((acc, v) => acc + v, 0)
    },
    renderAccessory() {
      return !isEmpty(this.accessoryProducts)
    },
    productAndRelatedIds() {
      return `${this.productId}|${this.relatedIds.join('|')}|${this.batteryUpgradeSkus.join('|')}`
    },
    filteredGroupPrices() {
      const productOrderBrandId = get(
        this.basketValues,
        ['customBasketAttributes', 'productOrderBrand'],
        '',
      )

      return productOrderBrandId ? pick(this.groupPrices, productOrderBrandId) : this.groupPrices
    },
    hasOneGroupPrice() {
      return Object.keys(this.filteredGroupPrices).length === 1
    },
    fromWarehouse: {
      get() {
        return this.asapFromAvailable
      },
      set(newValue) {
        const diff = this.fromWarehouse - newValue
        console.log('set fromWarehouse', this.fromWarehouse, newValue, diff, this.asap - diff)
        this.asap = this.asap - diff
        // this.fromWarehouse = this.asapFromAvailable
      },
    },
    dataLoading() {
      return this.availibilityPending || this.productDataPending
    },
    loading() {
      return this.basketPending || this.dataLoading
    },
    startValueAmount() {
      return this.startValueAsapFromAvailable + this.startValueDatesAmount || 1
    },
    startValueDatesAmount() {
      return (
        Object.values(this.startValueOrderCw).reduce((acc, amount) => acc + amount, 0) +
        get(this.basketValues, 'productOrderDate', []).reduce(
          (acc, { quantity }) => acc + quantity,
          0,
        )
      )
    },
    startValueAsapFromAvailable() {
      return get(this.basketValues, 'amountAsap', startData.asapFromAvailable)
    },
    startValueOrderCw() {
      return get(this.basketValues, 'productOrderCw', []).reduce((acc, { key, quantity }) => {
        acc[key] = quantity
        return acc
      }, {})
    },
    basketOrderCustomAttributes() {
      return get(this.basketValues, 'productOrderCw', []).reduce(
        (acc, { key, customBasketAttributes }) => {
          acc[key] = customBasketAttributes
          return acc
        },
        get(this.basketValues, 'productOrderDate', []).reduce(
          (acc, { key, customBasketAttributes }) => {
            acc[key] = customBasketAttributes
            return acc
          },
          { asap: get(this.basketValues, 'customBasketAttributes', {}) },
        ),
      )
    },
    resolvedBasketAttributes() {
      return mapKeys(
        isEmpty(this.basketAttributes)
          ? get(this.requestedBasketAttributes, [this.productId, 'basketAttributes'], {})
          : this.basketAttributes,
        (v, k) => camelCase(k),
      )
    },
    productOrderDateAttribute() {
      return get(this.resolvedBasketAttributes, camelCase(BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_DATE))
    },
    productOrderCwAttribute() {
      return get(this.resolvedBasketAttributes, camelCase(BIKE_ATTRIBUTES.TYPE.PRODUCT_ORDER_CW))
    },
    showDateSelectionSwitch() {
      return (
        (this.productOrderDateAttribute && this.availableStock && this.deliveryDateMax) ||
        (this.productOrderCwAttribute && this.futureStock)
      )
    },
    canAddAnotherAvailableEntry() {
      return (
        (this.remainingAsapFromAvailable || this.asapFromAvailable < this.availableStock) &&
        this.available.every(entry => entry.amount > 0)
      )
    },
    currentStock() {
      return this.availableStock - this.dateTotal
    },
    remainingAsapFromAvailable() {
      return this.asapFromAvailable - this.dateTotal
    },
    dateTotal() {
      return this.available.reduce((acc, value) => acc + parseInt(value.amount, 10), 0)
    },
    futureAsapTotal() {
      return Object.values(this.future).reduce((acc, item) => acc + parseInt(item.asap, 10), 0)
    },
    futureTotal() {
      return Object.values(this.future).reduce(
        (acc, item) => acc + (parseInt(item.amount, 10) || 0),
        0,
      )
    },
    futureStock() {
      return Object.values(this.future).reduce((acc, item) => acc + item.stock, 0)
    },
    futureStockText() {
      return this.calculateStocklevel(this.futureStock)
    },
    asapValue() {
      console.log(`asapValue: ${this.asap} -> ${this.asap === '' ? 0 : parseInt(this.asap, 10)}`)
      return this.asap === '' ? 0 : parseInt(this.asap, 10)
    },
    totalAmount() {
      return this.asapValue
    },
    totalStockAvailable() {
      return this.availableStock + this.futureStock
    },
    asapMax() {
      return this.totalStockAvailable
    },
    asapMaxText() {
      return this.calculateStocklevel(this.asapMax)
    },
    asapAvailable() {
      return this.totalStockAvailable - this.totalAmount
    },
    requestedBasketAvailability() {
      return get(this.requestedBasketAttributes, [this.productId, 'availabilities'])
    },
    availabilitySkus() {
      return [this.code, ...this.batteryUpgradeSkus]
    },
    availabilities() {
      const availabilities =
        Object.keys(this.availabilityProp).length === this.availabilitySkus.length
          ? this.availabilityProp
          : this.requestedBasketAvailability
          ? this.requestedBasketAvailability
          : this.availabilitySkus.length
          ? this.getAvailability(this.availabilitySkus)
          : {}
      return availabilities || {}
    },
    availability() {
      return this.availabilities?.[this.code] || []
    },
    firstAvailability() {
      return head(this.availability || [])
    },
    stockLimit() {
      return min(
        this.companyCodes
          .map(
            c =>
              AVAILABILITY_LIMITS?.[c]?.[this.articleType] ||
              AVAILABILITY_LIMITS['default']?.[this.articleType] ||
              0,
          )
          .filter(limit => limit),
      )
    },
    availableStock() {
      return this.hasSoldOutSelling
        ? soldOutSellingMax
        : this.isFirstAvailabilityAvailable
        ? get(this.firstAvailability, 'stock-level', 0)
        : 0
    },
    availableStockText() {
      return this.calculateStocklevel(this.availableStock)
    },
    isFirstAvailabilityAvailable() {
      return (
        this.firstAvailability &&
        ([
          AVAILABILITY_CONSTANTS.TYPES.AVAILABLE,
          AVAILABILITY_CONSTANTS.TYPES.FEW_AVAILABLE,
          AVAILABILITY_CONSTANTS.TYPES.MAYBE_AVAILABLE,
        ].includes(get(this.firstAvailability, AVAILABILITY_CONSTANTS.STATUS)) ||
          (this.hasSoldOutSelling &&
            [AVAILABILITY_CONSTANTS.TYPES.SOLD_OUT].includes(
              get(this.firstAvailability, AVAILABILITY_CONSTANTS.STATUS),
            )))
      )
    },
    firstAvailabilityIsReal() {
      return (
        this.firstAvailability &&
        [
          AVAILABILITY_CONSTANTS.TYPES.AVAILABLE,
          AVAILABILITY_CONSTANTS.TYPES.FEW_AVAILABLE,
        ].includes(get(this.firstAvailability, AVAILABILITY_CONSTANTS.STATUS))
      )
    },
    hasFutureAvailabilities() {
      return this.availability.length > 1
    },
    futureAvailabilities() {
      return (this.isFirstAvailabilityAvailable ? tail(this.availability) : this.availability).map(
        item => {
          const week = `${item.week}`.padStart(2, '0')
          return {
            stock: this.hasSoldOutSelling ? soldOutSellingMax : parseInt(item['stock-level'], 10),
            week,
            monday: setWeek(
              setDay(setYear(new Date(), item.year), 1),
              item.week,
              iso8601WeekSettings,
            ),
            year: `${item.year}`.slice(2),
            id: `${item.year}-W${week}`,
          }
        },
      )
    },
    basketData() {
      const asapAmount = this.futureAsapTotal + this.remainingAsapFromAvailable
      const data = []
      if (asapAmount > 0) {
        const additionalAttributes = this.filteredCustomAttributesByKey('asap')

        const entry = { quantity: asapAmount }
        if (!isEmpty(additionalAttributes)) {
          entry['custom'] = additionalAttributes
        }
        data.push(this.getBasketProductEntry(entry))
      }

      const availableEntries = this.productOrderDateAttribute
        ? this.available.filter(entry => (parseInt(entry.amount, 10) || 0) > 0)
        : []
      console.log({ availableEntries })
      availableEntries.forEach(entry => {
        const additionalAttributes = this.filteredCustomAttributesByKey(entry.date)

        data.push(
          this.getBasketProductEntry({
            quantity: parseInt(entry.amount, 10),
            custom: { ...additionalAttributes, [this.productOrderDateAttribute]: entry.date },
          }),
        )
      })

      const futureCwEntries = this.productOrderCwAttribute
        ? Object.values(this.future).filter(entry => (parseInt(entry.amount, 10) || 0) > 0)
        : []
      futureCwEntries.forEach(entry => {
        const additionalAttributes = this.filteredCustomAttributesByKey(entry.id)
        data.push(
          this.getBasketProductEntry({
            quantity: parseInt(entry.amount, 10),
            custom: { ...additionalAttributes, [this.productOrderCwAttribute]: entry.id },
          }),
        )
      })

      Object.entries(this.accessoryBasketData).forEach(
        ([productId, { amount, productOrderBrandId, customBasketAttributes, client }]) => {
          data.push(
            this.getBasketProductEntry(
              {
                quantity: parseInt(amount, 10),
                custom: Object.values(customBasketAttributes).reduce(
                  (acc, a) => {
                    acc[a.id] = a.value
                    return acc
                  },
                  { [productOrderBrandId]: client },
                ),
              },
              productId,
            ),
          )
        },
      )

      return {
        data,
      }
    },
    accessoryBasketData() {
      return Object.entries(this.accessories).reduce((acc, [productId, amount]) => {
        const product = this.accessoryProducts.find(i => i.id === productId)
        const productOrderBrandId = product?.basketAttributes?.ProductOrderBrand
        const customBasketAttributes = product?.customBasketAttributes || {}
        const client = head(Object.keys(product?.groupPrices || {}))
        acc[productId] = {
          amount,
          productOrderBrandId,
          customBasketAttributes,
          client,
        }
        return acc
      }, {})
    },
  },
  watch: {
    productId: {
      handler() {
        this.accessories = {}
        if (this.batteryUpgradeChoice !== this.startValueBatteryUpgradeChoice) {
          this.batteryUpgradeChoice = this.startValueBatteryUpgradeChoice
        }
      },
    },
    asapValue: {
      handler() {
        console.log(
          `watch asapValue: ${this.asapValue} (futureTotal:${this.futureTotal}, asapMax: ${this.asapMax}, totalStockAvailable: ${this.totalStockAvailable}, availableStock: ${this.availableStock}, futureStock: ${this.futureStock},)`,
        )
        this.$emit('update-asap-value', this.asapValue)
        if (this.asapValue < this.futureTotal) {
          this.asap = this.futureTotal
        } else if (this.asapValue > this.asapMax) {
          this.asap = this.asapMax
        }
      },
      immediate: true,
    },
    productAndRelatedIds: {
      async handler() {
        console.log(
          `watch productId: ${this.productId} (startValueAmount:${this.startValueAmount})`,
        )

        if (isUndefined(this.productId)) {
          this.showDateSelection = false
          return
        }

        if (isEmpty(this.basketAttributes) && this.code) {
          await this.requestProductData(isEmpty(this.availability))
          // this.getProductData([this.code])
        } else {
          this.requestedBasketAttributes = {}
          if (this.code && isEmpty(this.availability)) {
            // this.fetchAvailability({ sku: this.code })
            await this.requestAvailibility()
          }
        }
        this.updateFutureEntries() // Needed to set futureTotal! -> maxium asap value!!
        this.asap = this.startValueAmount
        this.asapFromAvailable = startData.asapFromAvailable
        this.$nextTick(() => {
          this.resetAvailable()
          this.resetFutureAmounts()
          this.updateFutureEntries()
          const el = this.$refs.asapInput.$el.querySelector('input')
          if (el) {
            el.focus()
          }
          this.showDateSelection = !!this.startValueDatesAmount
        })
      },
      immediate: true,
    },
    futureAvailabilities: {
      handler() {
        this.updateFutureEntries()
      },
      immediate: true,
    },
    dateTotal() {
      this.calculateAsapDistribution()
      this.calculateDateMaxEntries()
    },
    totalAmount: {
      handler() {
        this.calculateAsapDistribution()
        this.calculateDateMaxEntries()
      },
      immediate: true,
    },
    futureTotal() {
      this.calculateAsapDistribution()
      this.calculateDateMaxEntries()
    },
    showDateSelection(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.resetAvailable(true)
        this.resetFutureAmounts(true)
      }
    },
  },
  methods: {
    ...mapActions(STORE_MODULES.AIMEOS, [AIMEOS_ACTIONS.BASKET_REQUEST]),
    async requestAvailibility() {
      this.availibilityPending = true
      await this.fetchSkuAvailability({
        sku: this.availabilitySkus.map(sku => [sku, head(this.companyCodes)]),
      })
      this.availibilityPending = false
    },
    async submit(e, brand) {
      this.basketPending = true

      this.submitBrand = brand ? brand : head(Object.keys(this.groupPrices))

      e.preventDefault()

      try {
        if (this.relatedIds.length) {
          console.log('remove products')
          await this.removeProducts()
        } else {
          // Track adding to basket, not updating, as defined in https://docs.google.com/spreadsheets/d/1yKo69YY5TaJg1ZCxpXkxT8L7Qc8OrkKBilNtNRbqYys
          this.addToBasketCallback()
        }

        await this[AIMEOS_ACTIONS.BASKET_REQUEST]({
          method: 'POST',
          url: this.basketLinkUrl,
          data: this.basketData,
        })
        this.submitError = false
        this.basketPending = false
        this.$emit('close')
      } catch (error) {
        console.log(error)
        this.submitError = true
        this.basketPending = false
      }
    },
    async removeProducts() {
      if (!this.basketLinkUrl) {
        return
      }

      await this[AIMEOS_ACTIONS.BASKET_REQUEST]({
        method: 'DELETE',
        url: this.basketLinkUrl,
        data: {
          data: this.relatedIds.map(id => ({
            id,
          })),
        },
      })
    },
    resetAvailable(clearValues) {
      const startEntries = get(this.basketValues, 'productOrderDate', [])
      this.available = clearValues
        ? []
        : startEntries.map(item => {
            const entry = this.getNewDateEntry()
            const amount = get(item, 'quantity', 0)
            // ToDo: Check minDate?
            entry.date = get(item, 'key', this.minDate)
            entry.amount = amount
            entry.max = amount
            return entry
          })
    },
    handleCwAmountChange(item) {
      if (isNull(item.amount)) {
        this.$set(item, 'amount', 0)
      }
    },
    addNewDateEntry() {
      if (!this.remainingAsapFromAvailable) {
        this.fromWarehouse = this.fromWarehouse + 1
      }
      this.available.push(this.getNewDateEntry())
    },
    removeDateEntry(index) {
      this.$delete(this.available, index)
    },
    getNewDateEntry() {
      return {
        date: this.minDate,
        amount: 1,
        id: uuidv1(),
        dateMenu: false,
        max: this.remainingAsapFromAvailable,
      }
    },
    formatDate(date) {
      const parsed = isEmpty(date) ? '' : parseISO(date) || ''
      return parsed === '' ? '' : this.$d(parsed)
    },
    allowedDates(val) {
      const date = parseISO(val)

      return [1, 2, 3, 4, 5].includes(getDay(date))
    },
    calculateAsapDistribution() {
      console.log('calculateAsapDistribution')
      console.log({ asapValue: this.asapValue, futureTotal: this.futureTotal })
      if (this.asapValue < this.futureTotal) {
        console.log('increasee asap to futureTotal')
        this.asap = this.futureTotal
        return
      }
      const restAfterFixedFuture = this.asapValue - this.futureTotal
      console.log({ restAfterFixedFuture })
      this.asapFromAvailable =
        restAfterFixedFuture < this.availableStock ? restAfterFixedFuture : this.availableStock
      let amount = restAfterFixedFuture - this.asapFromAvailable
      console.log('asap from available', this.asapFromAvailable)
      console.log('asap rest', amount)

      Object.values(this.future).forEach(item => {
        const availableForAsap = item.stock - item.amount
        item.asap = amount < availableForAsap ? amount : availableForAsap

        amount = amount - item.asap
        console.log(`asap from ${item.id}`, item.asap)
        console.log('asap rest', amount)
      })
      if (amount > 0) {
        console.log('asap is too large.. reducing')
        this.asap = this.asap - amount
      }
    },
    calculateDateMaxEntries() {
      console.log('calculateDateMaxEntries')
      let max = this.asapFromAvailable
      console.log({ max })
      this.available = this.available.map(item => {
        console.log(max, item.max)
        item.max = max
        if (item.amount > max) {
          console.log('amount > max – reducing…')
          item.amount = max
        }
        max = max - item.amount
        return item
      })
    },
    updateFutureEntries() {
      console.log('updateFutureEntries')
      this.future = this.futureAvailabilities.reduce((acc, entry) => {
        const futureEntry = get(this.future, entry.id, {
          ...entry,
          stockText: this.calculateStocklevel(entry.stock),
          amount: 0,
          asap: 0,
        })
        this.$set(acc, entry.id, futureEntry)
        return acc
      }, {})
    },
    resetFutureAmounts(clearValues) {
      Object.values(this.future).forEach(entry => {
        entry.amount = clearValues ? 0 : get(this.startValueOrderCw, entry.id, 0)
      })
    },
    filteredCustomAttributesByKey(key) {
      const productOrderBrandId = get(this.resolvedBasketAttributes, 'productOrderBrand')
      const productUpgradeBatteryId = get(this.resolvedBasketAttributes, 'productUpgradeBattery')
      const additionalAttributes = productOrderBrandId
        ? { [productOrderBrandId]: this.submitBrand }
        : {}
      if (productUpgradeBatteryId && this.batteryUpgradeChoice && this.batteryUpgradeIdentifier) {
        additionalAttributes[productUpgradeBatteryId] = {
          [this.batteryUpgradeIdentifier]: this.batteryUpgradeChoice,
        }
      }
      return Object.entries(get(this.basketOrderCustomAttributes, key, {})).reduce(
        (acc, [k, v]) => {
          const id = get(this.resolvedBasketAttributes, camelCase(k))
          if (!isUndefined(id) && !isUndefined(v)) {
            acc[id] = v
          }
          return acc
        },
        Object.values(this.customBasketAttributes).reduce((acc, attr) => {
          acc[attr.id] = attr.value
          return acc
        }, additionalAttributes),
      )
    },
    accessoryAmountChange({ id, amount }) {
      this.$set(this.accessories, id, amount)
    },
    calculateStocklevel(stock) {
      return this.stockLimit && stock > this.stockLimit ? `${this.stockLimit}+` : `${stock}`
    },
  },
}
</script>

<style lang="scss" module>
.content {
  position: relative;

  font-size: var(--font-base);
}

.bikeForm {
  position: sticky;
  top: 1rem;
}

.code {
  font-weight: var(--font-weight-normal);
  font-size: 0.875rem;
}

.actions {
  border-top: var(--border-grey-lighten-1);
}

.actionsTop {
  border-bottom: var(--border-grey-lighten-1);
}

.accessoryCard {
  @media screen and (min-width: map-get($grid-breakpoints, 'lg')) {
    border-top: none;
    border-left: var(--border-grey-lighten-1);
  }
  border-top: var(--border-grey-lighten-1);
}

.splitArea {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    display: grid;
    grid-template-areas:
      'headerSendAsap headerDateSel'
      'availableAsap dateSelAvailable'
      'futureAsap dateSelFuture';
    grid-template-columns: 1fr 1fr;

    column-gap: 1rem;
  }

  padding-top: 1rem;
}

.headerSendAsap {
  grid-area: headerSendAsap;
}

.availableAsap {
  grid-area: availableAsap;
}

.futureAsap {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    border-top: var(--border-grey-lighten-1);
  }

  grid-area: futureAsap;
  margin: 0;
  padding: 1rem 0 0 0 !important;

  li {
    margin: 0;
    padding: 0;
  }
}

.asapContainer {
  min-width: 11rem;
  max-width: 16rem;
}

.headerDateSel {
  @media screen and (max-width: map-get($grid-breakpoints, 'md')) {
    padding-top: 1rem;

    border-top: var(--border-grey-lighten-1);
  }

  grid-area: headerDateSel;
}

.dateSelAvailable {
  grid-area: dateSelAvailable;
}

.dateSelFuture {
  @media screen and (min-width: map-get($grid-breakpoints, 'sm')) {
    display: grid;
    grid-area: dateSelFuture;
    grid-gap: 1rem;
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    border-top: var(--border-grey-lighten-1);
  }

  margin: 0;
  padding: 1rem 0 0 0 !important;

  li {
    margin: 0;
    padding: 0;
  }
}

.showDateSelectionLabel {
  color: var(--text) !important;
}

.amountButton {
  min-width: 12rem;
}

.price {
  color: var(--white);

  dt {
    padding: 4px;

    color: var(--text);

    background: var(--white);
    border-radius: 100px;
  }
}

.groupPrices {
  display: grid;
  grid-area: dateSelAvailable;
  grid-template-columns: minmax(max-content, 1rem);
  justify-self: end;

  text-align: right;

  dd {
    display: flex;
    align-items: center;

    font-size: 1rem;
  }
}

.batteryOptions {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    padding: 1rem 1.25rem 0 1rem;

    font-size: 1rem;

    border: 1px solid var(--grey-lighten-1);
    border-radius: 5px;
  }
  width: fit-content;
  padding: 0.5rem 0 0;

  border-top: 1px solid var(--grey-lighten-1);
  border-bottom: 1px solid var(--grey-lighten-1);

  :global(.v-radio .v-label) {
    margin-left: 0;
  }
}

.batteryOption {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    min-width: 9rem;

    font-size: 1rem;
  }

  font-size: 0.75rem;
}
</style>
